/**
 * @file
 * Inspiration Card – "already seen" behavior.
 *
 * @description
 * Tracks which inspiration cards the user has clicked during the current session.
 * The visited URL is stored in sessionStorage under the key `tp-seen-inspiration-cards`.
 * On each page load (and after AJAX updates) every card whose URL is in that
 * set receives the `inspiration-card--seen` modifier class, which reveals a
 * visual "Already seen" badge in the top-right corner of the card media area.
 */

((Drupal, once) => {
  'use strict';

  /** sessionStorage key used to persist seen card URLs. */
  const STORAGE_KEY = 'tp-seen-inspiration-cards';

  /**
   * Returns the current set of seen card URLs from sessionStorage.
   */
  const getSeenCards = (): Set<string> => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  };

  /**
   * Persists the given set of seen card URLs to sessionStorage.
   */
  const saveSeenCards = (seen: Set<string>): void => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
    } catch {
      // sessionStorage unavailable (e.g., private browsing quota exceeded) – fail silently.
    }
  };

  /**
   * Marks a single card element as seen by adding the BEM modifier class.
   */
  const markAsSeen = (card: HTMLElement): void => {
    card.classList.add('inspiration-card--seen');

    const badge = card.querySelector<HTMLElement>('.inspiration-card__seen-badge');
    if (badge) {
      badge.removeAttribute('hidden');
    }
  };

  /**
   * Initializes the "already seen" behavior for inspiration cards.
   *
   * @description
   * - On load: restores seen state from sessionStorage for all cards on the page.
   * - On click: records the card URL in sessionStorage and applies the seen modifier.
   */
  Drupal.behaviors.tpInspirationCardSeen = {
    attach(context: Document | HTMLElement): void {
      const cards = once('tp-inspiration-card-seen', '.inspiration-card[data-card-url]', context);
      const seen = getSeenCards();

      cards.forEach((el) => {
        const card = el as HTMLElement;
        const cardUrl = card.dataset.cardUrl ?? '';

        // Restore seen state from sessionStorage.
        if (cardUrl && seen.has(cardUrl)) {
          markAsSeen(card);
        }

        // Record the card as seen when the user clicks it.
        card.addEventListener('click', () => {
          if (!cardUrl) return;

          const current = getSeenCards();
          current.add(cardUrl);
          saveSeenCards(current);
          markAsSeen(card);
        });
      });
    },
  };
})(Drupal, once);
